import React, { useEffect } from "react";
import { Button, Card, Popconfirm, Space, Table, Tag } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  ManageCompany,
  ManagePatients,
  ManagePriceBrackets
} from "src/shared/constants/permissions-enum";
import hasPermission from "src/shared/functions/access-control";
import { useNavigate } from "react-router-dom";
import {
  setDataTableDataGrid,
  setIsLoadingTableDataGrid,
  setRefreshTableDataGrid,
  setTotalTableDataGrid,
} from "../../domain/app/redux/data-grid/data-grid-slice";
import { useDispatch, useSelector } from "react-redux";
import { priceBracketService } from "../../shared/services/price-brackets";
import { getRefreshTableDataGrid } from "../../domain/app/redux/data-grid/data-grid-selectors";
import AppPageMetadata from "../../domain/core/AppPageMetadata";
import DataGrid from "../../shared/components/data-grid";
import Error403 from "../errorPages/Error403";
import { PriceBracketDto } from "../../shared/services/price-brackets/dto";
import { CountryDto } from "../../shared/common-dtos/country-dto";

const PriceBracketsList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const refreshTable = useSelector(getRefreshTableDataGrid);
  const { messages } = useIntl();

  useEffect(() => {
    fetchPriceBrackets();
  }, [refreshTable]);

  const fetchPriceBrackets = async () => {
    if (hasPermission(ManagePatients.List)) {
      try {
        dispatch(setIsLoadingTableDataGrid(true));
        await priceBracketService.getAllPriceBrackets().then((response) => {
          const data = response?.Data?.length > 0 ? response?.Data : [];
          dispatch(setDataTableDataGrid(data));
          dispatch(setTotalTableDataGrid(response?.Data?.length));
        });
      } catch (error) {
        console.error(error);
      } finally {
        dispatch(setIsLoadingTableDataGrid(false));
      }
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await priceBracketService.deletePriceBracket(id);
      dispatch(setRefreshTableDataGrid(!refreshTable));
    } catch (error) {
      console.error(error);
    }
  };

  const columns = [
    {
      title: <FormattedMessage id="priceBrackets.name" defaultMessage="Name" />,
      dataIndex: "name",
      key: "name",
    },
    {
      title: (
        <FormattedMessage id="priceBrackets.price" defaultMessage="Price" />
      ),
      dataIndex: "price",
      key: "price",
      render: (price: number) => `$${price?.toFixed(2)}`,
    },
    {
      title: (
        <FormattedMessage
          id="priceBrackets.perCount"
          defaultMessage="Per Count"
        />
      ),
      dataIndex: "perCount",
      key: "perCount",
    },
    {
      title: (
        <FormattedMessage id="priceBrackets.status" defaultMessage="Status" />
      ),
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? (
            <FormattedMessage id="common.active" defaultMessage="Active" />
          ) : (
            <FormattedMessage id="common.inactive" defaultMessage="Inactive" />
          )}
        </Tag>
      ),
    },
    {
      title: (
        <FormattedMessage
          id="priceBrackets.countries"
          defaultMessage="Countries"
        />
      ),
      dataIndex: "countries",
      key: "countries",
      render: (countries: CountryDto[]) => (
        <>
          {countries?.map((country) => (
            <Tag key={country.id}>{country.name}</Tag>
          ))}
        </>
      ),
    },
    {
      title: <FormattedMessage id="common.actions" defaultMessage="Actions" />,
      key: "actions",
      render: (text: string, record: PriceBracketDto) => (
        <Space size="middle">
          {hasPermission(ManagePriceBrackets.Update) && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/price-brackets/edit/${record.id}`)}
              style={{ padding: 0 }}
            />
          )}
          {hasPermission(ManagePriceBrackets.Delete) && (
            <Popconfirm
              title={
                <FormattedMessage
                  id="common.deleteConfirmation"
                  defaultMessage="Are you sure you want to delete this item?"
                />
              }
              onConfirm={() => handleDelete(record.id)}
              okText={<FormattedMessage id="common.yes" defaultMessage="Yes" />}
              cancelText={
                <FormattedMessage id="common.no" defaultMessage="No" />
              }
            >
              <Button type="link" danger icon={<DeleteOutlined />} style={{ padding: 0 }} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {hasPermission(ManagePriceBrackets.List) ? (
        <AppPageMetadata
          title={messages["priceBrackets.sideBarName"].toString()}
        >
          <DataGrid
            title="priceBrackets.sideBarName"
            titleTable="priceBrackets.title"
            tableColumn={columns}
            addUrl='/price-brackets/create'
            labelAddButton={hasPermission(ManageCompany.Create)
              ? 'priceBrackets.addNewPriceBracket'
              : undefined}
          />
        </AppPageMetadata>
      ) : (
        <Error403 />
      )}
    </>
  );
};

export default PriceBracketsList;
